import { array, predicate, record } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { cond, constant, overEvery, T } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Restriction, RestrictionType } from '@mews-commander/data-access';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { ApplicationState, WithServiceId, WithTimeUnit } from '@mews-commander/types';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText, TimeService, useDateTimeLocalizationInfo } from '@mews/framework';
import { Typography, Divider, Icon, IconName, padStartZero, Stack } from '@optimus-web/core';
import { createScopedActions, useCommanderSelector } from '@mews-commander/utils';
import {
    composeServiceIdFromProps,
    getServiceTimeUnit,
    isDayUseService,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { closeSidebar as closeSidebarAction, selectBodyCell as selectBodyCellAction } from '../../../actions';
import { isCellSelected, isSidebarOpened } from '../../../selectors';
import { getCellExceptions, getCellStatus } from '../../../utils';
import { CellStatus } from '../../../utils/enums';
import { getOpenHoursIntervals } from '../../../utils/restrictions';
import { RestrictionsBodyCellElement } from '../styles';
import { RestrictionCalendarRowConditions } from '../../../types';
import CellExceptionsInfo from './CellExceptionsInfo';

const existExceptions = flow(getCellExceptions, predicate.not(record.isEmpty));
const areRestrictionsEmpty = ({ restrictions }: { restrictions: Array<Pick<Restriction, 'exceptions'>> }) =>
    array.isEmpty(restrictions);

const CellState = ({ textKey }: $TSFixMe) => (
    <Typography textStyle="bodySmall" as="div">
        <LocalizedText textKey={textKey} />
    </Typography>
);

const handleBodyCellContent = cond<
    Parameters<typeof existExceptions>[0] & { isServiceDayUse: boolean },
    JSX.Element | null
>([
    [areRestrictionsEmpty, constant(<CellState textKey={TextKey.Open} />)],
    [
        existExceptions,
        (props) => (
            <CellExceptionsInfo
                exceptions={getCellExceptions(props)}
                timeUnit={props.timeUnit}
                isServiceDayUse={props.isServiceDayUse}
            />
        ),
    ],
    [T, (props) => (props.hasOpenHoursIntervals ? null : <CellState textKey={TextKey.Closed} />)],
]);

const cellRowIconMap = {
    [RestrictionType.ClosedToStay]: IconName.GuestInHouse,
    [RestrictionType.ClosedToArrival]: IconName.Arrivals,
    [RestrictionType.ClosedToDeparture]: IconName.Departures,
};

interface RestrictionsBodyCellProps extends WithTimeUnit, WithServiceId {
    cellRestrictions: Array<Restriction>;
    cellConditions: RestrictionCalendarRowConditions;
    closeSidebar: $TSFixMeFunction;
    columnIndex: number;
    isSelected: boolean;
    rowIndex: number;
    selectBodyCell: $TSFixMeFunction;
}

const RestrictionsBodyCell = ({
    cellRestrictions,
    columnIndex,
    closeSidebar,
    isSelected,
    rowIndex,
    selectBodyCell,
    timeUnit,
    serviceId,
    cellConditions,
    ...matrixCellProps
}: RestrictionsBodyCellProps) => {
    const handleOnClick = () => {
        if (!isSelected) {
            selectBodyCell({ cellRestrictions, columnIndex, rowIndex });
        } else {
            closeSidebar();
        }
    };
    const openHoursIntervals = getOpenHoursIntervals(cellRestrictions);
    const hasOpenHoursIntervals = openHoursIntervals.length > 0;

    const isServiceDayUse = useCommanderSelector((state) => isDayUseService(state, { id: serviceId }));
    const localization = useDateTimeLocalizationInfo();

    const renderOpenHoursIntervals = () =>
        openHoursIntervals.map((interval) => {
            const [start, end] = interval.map((hour: number) => {
                const srtTime = `${padStartZero(hour)}:00:00`;
                const time = TimeService.parse(srtTime);
                return TimeService.toShortString(time, localization);
            });

            const value = `${start} ${UnicodeEntity.Dash} ${end}`;

            return (
                <Typography textStyle="bodySmall" key={value} as="div">
                    {value}
                </Typography>
            );
        });

    const hasExceptions = existExceptions({ restrictions: cellRestrictions, timeUnit });
    const cellStatus = getCellStatus({ cellRestrictions, hasExceptions, hasOpenHoursIntervals });

    return (
        <RestrictionsBodyCellElement
            data-test-id={DataTestId.RestrictionCalendarBodyCell}
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            hasExceptions={hasExceptions}
            isClosed={cellStatus === CellStatus.Closed}
            isOpened={cellStatus === CellStatus.Open}
            isSelected={isSelected}
            onClick={handleOnClick}
            serviceId={serviceId}
            vertical
            distribution="space-around"
            {...matrixCellProps}
        >
            <Stack itemAlignment={cellStatus === CellStatus.Exceptions ? 'start' : 'center'} spacing="100">
                <Icon
                    value={cellRowIconMap[cellConditions.type]}
                    variant={cellStatus === CellStatus.Closed ? 'danger' : 'success'}
                />
                <Stack vertical spacing="50">
                    {hasOpenHoursIntervals && renderOpenHoursIntervals()}
                    {hasOpenHoursIntervals && hasExceptions ? <Divider /> : null}

                    {handleBodyCellContent({
                        restrictions: cellRestrictions,
                        timeUnit,
                        hasOpenHoursIntervals,
                        isServiceDayUse,
                    })}
                </Stack>
            </Stack>
        </RestrictionsBodyCellElement>
    );
};

export default connect(
    createStructuredSelector<ApplicationState, { isSelected: boolean }>({
        isSelected: overEvery([isCellSelected, isSidebarOpened]),
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        timeUnit: composeServiceIdFromProps(getServiceTimeUnit),
    }),
    createScopedActions(
        {
            closeSidebar: closeSidebarAction,
            selectBodyCell: selectBodyCellAction,
        },
        { scopeName: 'serviceId' }
    )
)(RestrictionsBodyCell);
